/** @format */

import { useState } from "react";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";
import { useNavigate, useOutletContext } from "fusion-plugin-react-router";
import PortalCard from "../../components/dashboard/PortalCard";
import { deformatDateT } from "../../../../shared/utils/formatters";
import Loader from "../../../../shared/components/Loader";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import {
  cloneQuote,
  fetchPanelData,
  fetchRiskData,
} from "../../../../features/quote/services/quoteService";
import QuoteStep from "../../../quote/enums/QuoteStep";
import { fetchArchiveList } from "../../../buy/services/buyService";
import { TAXI_QUOTE_BRANDREF } from "../../../../config/config";
import { assetsPath } from "../../../../shared/utils/assetsPath";
import PolicyCard from "../../components/dashboard/PolicyCard";
import PolicyDateTimeModal from "../../components/dashboard/PolicyDateTimeModal";

const HomePage = () => {
  // Get dashboard data from Outlet context
  const { indicator, foreName, error } = useOutletContext();

  const css = assetUrl("../../../../styles/landing.css");
  const policyCss = assetUrl("../../../../styles/policyDetails.css");

  let STATE = useAppStateStore();
  const brandRef = TAXI_QUOTE_BRANDREF;
  const FileIcon = assetUrl("../../../../assets/icons/file-blue.svg");
  const navigate = useNavigate();

  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [riskLoading, setRiskLoading] = useState(false);
  const [documentsloading, setDocumentsloading] = useState(false);

  const customerPolicies = STATE.customerPolicies;
  const customerQuotes = STATE.customerQuotes;

  // --- Quote navigation logic (unchanged) ---
  const fetchPanel = async () => {
    try {
      await fetchPanelData(
        null,
        customerQuotes.riskObjectId,
        customerQuotes.startDate
      );
      await fetchRiskData(customerQuotes.riskObjectId, QuoteStep.TQ_PANEL);
    } catch (error) {
      console.error("Error fetching panel data:", error);
    }
  };

  const QuoteNavigationParent = () => {
    const isStartDateInPast = new Date(customerQuotes.startDate) < new Date();
    if (
      isStartDateInPast &&
      customerQuotes.quoteStatus === "ActiveWithin30Days"
    ) {
      setIsPolicyModalOpen(true);
    } else {
      handleQuoteNavigation();
    }
  };

  const activeWithin30Days = async () => {
    STATE.setCurrentStep(QuoteStep.TQ_PANEL_LOADER);
    navigate(`/taxi/quote?code=${sessionStorage.getItem("uberCode")}`);
    await fetchPanel();
  };

  const inactiveWithin31To60Days = async () => {
    setRiskLoading(true);
    await fetchRiskData(customerQuotes.riskObjectId, QuoteStep.TQ1);
    setRiskLoading(false);
    navigate(`/taxi/quote?code=${sessionStorage.getItem("uberCode")}`);
  };

  const inactive60DaysPlus = async () => {
    setRiskLoading(true);
    await cloneQuote(customerQuotes.riskObjectId, QuoteStep.TQ1);
    setRiskLoading(false);
    navigate(`/taxi/quote?code=${sessionStorage.getItem("uberCode")}`);
  };

  const handleQuoteNavigation = async () => {
    if (customerQuotes.quoteStatus === "ActiveWithin30Days") {
      await activeWithin30Days();
    } else if (customerQuotes.quoteStatus === "InactiveWithin31To60Days") {
      await inactiveWithin31To60Days();
    } else if (customerQuotes.quoteStatus === "Inactive60DaysPlus") {
      await inactive60DaysPlus();
    }
  };

  const handlePolicyDocuments = async () => {
    const customerPoliciesData = customerPolicies?.customerPolicies?.[0];
    const requestBody = {
      riskObjectId: customerPoliciesData.riskObjectId,
      riskObjectHistoryId: customerPoliciesData.riskObjectHistoryId,
      BrandRef: brandRef,
      documentTagStage: "All",
      documentTagCategory: "PortalDocuments",
      LatestVaultArchivedItemsOnly: true,
    };
    setDocumentsloading(true);
    try {
      const response = await fetchArchiveList(requestBody);
      STATE.setDocuments(response);
      navigate(`/documents`);
    } catch (error) {
      console.error("Failed to fetch the archiv />e vault list:", error);
    } finally {
      setDocumentsloading(false);
    }
  };

  // --- Render logic based on indicator ---
  let content = null;
  if (indicator === "Policy") {
    content = customerPolicies?.customerPolicies?.length ? (
      customerPolicies.customerPolicies.map((policy, index) => (
        <PolicyCard
          key={policy?.policyNumber}
          Logo={policy?.schemeTableId}
          FileIcon={FileIcon}
          navigate={navigate}
          policyStatus={policy?.policyStatus}
          carName={policy?.vehicleMake}
          carReg={policy?.registrationNumber}
          policyNumber={policy?.policyNumber}
          expiryDate={deformatDateT(policy?.expiryDate)}
          isFirst={index === 0}
          onClick={handlePolicyDocuments}
        />
      ))
    ) : (
      <div className="grid">
        <PortalCard
          heading={"No active policies"}
          text={"You currently have no active policies."}
          image={assetsPath.heart}
          className="width-210"
        />
      </div>
    );
  } else if (indicator === "Quote") {
    content = (
      <div className="grid">
        <PortalCard
          heading={"View my quotes"}
          text={"View all your quotes before they expire"}
          image={assetsPath.heart}
          onClick={QuoteNavigationParent}
          className="width-210"
        />
      </div>
    );
  } else {
    content = (
      <div className="grid">
        <PortalCard
          heading={"Get a new quote"}
          text={"Start a new quote for your insurance needs."}
          image={assetsPath.heart}
          onClick={() => navigate("/taxi/quote")}
          className="width-210"
        />
      </div>
    );
  }

  return (
    <>
      {documentsloading && <Loader text="Fetching your policy documents" />}
      {riskLoading && <Loader text="Fetching data..." />}

      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <link rel="stylesheet" href={css} />
        <link rel="stylesheet" href={policyCss} />
      </Helmet>

      <p className="text-757575 margin-bottom-15 capitalize">Hi, {foreName}</p>
      <h1 className="welcome-text">
        {indicator === "Policy"
          ? "Driver Insurance"
          : indicator === "Quote"
          ? "Ready to purchase your quote?"
          : "Ready to start your quote?"}
      </h1>

      {content}
      {error && <div>{error}</div>}

      {isPolicyModalOpen && (
        <PolicyDateTimeModal
          onClose={() => setIsPolicyModalOpen(false)}
          riskObjectId={customerQuotes.riskObjectId}
          actions={handleQuoteNavigation}
        />
      )}
    </>
  );
};

export default HomePage;
